import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error internally (to console in dev, could be sent to monitoring service in prod)
    console.error("ErrorBoundary caught an error:", {
      message: error?.message,
      name: error?.name,
      timestamp: new Date().toISOString(),
      componentStack: errorInfo?.componentStack?.slice(0, 500) // Truncate for security
    });
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center px-6">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 border-4 border-black flex items-center justify-center mx-auto mb-6">
              <AlertTriangle size={32} strokeWidth={1.5} />
            </div>
            
            <h1 className="font-heading font-bold text-2xl sm:text-3xl uppercase mb-4">
              Something Went Wrong
            </h1>
            
            <p className="font-body text-zinc-600 mb-8">
              We encountered an unexpected error. Please try again or return to the homepage. If this problem persists, contact us at{" "}
              <a 
                href="mailto:info@blacklineestimating.com" 
                className="underline hover:text-black"
              >
                info@blacklineestimating.com
              </a>
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={this.handleRetry}
                className="w-full sm:w-auto bg-black text-white h-12 px-6 uppercase font-bold text-sm tracking-widest border-2 border-black btn-brutal flex items-center justify-center gap-2"
                data-testid="error-retry-button"
              >
                <RefreshCw size={18} />
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                className="w-full sm:w-auto bg-white text-black h-12 px-6 uppercase font-bold text-sm tracking-widest border-2 border-black hover:bg-zinc-100 transition-colors flex items-center justify-center gap-2"
                data-testid="error-home-button"
              >
                <Home size={18} />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
